// QCS-T5 — shared, record-ONLY display derivations for the delivery UI.
//
// Data-link rule (Annex Z.7/Z.10): a player's photo, name, position, age,
// market value and contract end come from the EXISTING player record — the same
// hydrated shape the proposal cards, deliver rows and player profile all read
// (PlayerMinimalSerializer: image / name / position / age / market_value +
// market_value_currency / contract_until). Nothing here free-types or accepts a
// chat-sourced value; it only formats fields that already sit on the record.
import { formatCompactMoney } from "../../utils/agentRequestFigures";

// Deterministic 2-letter initials for the photo fallback (never a random glyph).
export const playerInitials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

// "2027" from an ISO contract_until, or null. Year only — the card line is a
// glance, not a full date.
export const contractYear = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : String(d.getFullYear());
};

// The meta segments for a player, each rendered only when its field is present
// on the record: position · {age}y · {MV} · Contract {year}. Returns an array of
// strings so the caller controls the separators (a missing field never leaves a
// dangling "·").
export const playerMetaParts = (player) => {
  if (!player) return [];
  const parts = [];
  if (player.position) parts.push(player.position);
  if (player.age !== null && player.age !== undefined && player.age !== "") {
    parts.push(`${player.age}y`);
  }
  const mv = formatCompactMoney(player.market_value, player.market_value_currency || "EUR");
  if (mv) parts.push(mv);
  const year = contractYear(player.contract_until);
  if (year) parts.push(`Contract ${year}`);
  return parts;
};

// The player's OWN agent line, "Name · Agency" (Annex A6·b/B3), from the record's
// player_agent. Null when the player has no agent on file.
export const playerAgentLine = (playerAgent) => {
  if (!playerAgent || !playerAgent.name) return null;
  return playerAgent.agency
    ? `${playerAgent.name} · ${playerAgent.agency}`
    : playerAgent.name;
};
